import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { analyticsService } from '../services/analytics.service';
import type { DashboardData } from '@lnr-v2/types';

/**
 * Hook return type
 */
interface UseDashboardStatsReturn {
  data: DashboardData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching dashboard statistics and KPIs
 *
 * Features:
 * - Automatic data fetching on mount
 * - 5-minute cache for optimal performance
 * - Auto-refresh every 5 minutes to keep data current
 * - Background refetching when window regains focus
 * - Comprehensive error handling
 * - TypeScript type safety
 *
 * The hook fetches all dashboard data in a single optimized API call:
 * - Active projects count
 * - Total clients count
 * - Portfolio views
 * - Completion rate
 * - Recent activity feed (last 10 activities)
 * - Top 5 viewed cases
 *
 * @example
 * ```tsx
 * function DashboardPage() {
 *   const { data, loading, error, refetch } = useDashboardStats();
 *
 *   if (loading) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *   if (!data) return null;
 *
 *   return (
 *     <div>
 *       <h1>Active Projects: {data.kpis.activeProjects}</h1>
 *       <ActivityFeed activities={data.activities} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useDashboardStats = (): UseDashboardStatsReturn => {
  // Main query for fetching dashboard KPIs
  const {
    data: response,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.analytics.dashboard(),
    queryFn: analyticsService.getDashboardKPIs,
    // Cache settings
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    // Auto-refresh settings
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchOnMount: true, // Always fetch fresh data on mount
    // Retry settings
    retry: 3, // Retry failed requests 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Extract data from response or return null
  const data = response?.success && response.data ? response.data : null;

  // Convert query error to Error type for consistent error handling
  const error = queryError ? (queryError as Error) : null;

  // Wrapper for refetch to match expected signature
  const refetchWrapper = async () => {
    await refetch();
  };

  return {
    data,
    loading,
    error,
    refetch: refetchWrapper,
  };
};
